import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCartStore, cartItemOptionIds } from '@astro/store'
import { useAuth } from '@astro/auth'
import { api } from '@astro/api'
import { useToast } from '../context/ToastContext.jsx'

/** Unwrap `{ status, data }` envelopes and paginators into a plain line array. */
function toLines(res) {
  const d = res && typeof res === 'object' && 'data' in res ? res.data : res
  if (Array.isArray(d)) return d
  if (d && Array.isArray(d.data)) return d.data
  return []
}

/** Paise amount for a server cart line — prefers the line price, falls back to the product. */
function linePaise(line) {
  const raw = line.price ?? line.total_price ?? line.product?.total_price ?? line.product?.price
  const n = parseFloat(raw)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

/**
 * Keeps the local (persisted Zustand) cart and the server-side cart in sync
 * for signed-in users, and exposes qty/remove mutators that update both.
 *
 * Reconciliation policy, applied once per server fetch:
 *  - local item missing on server        → create it upstream (addToCart)
 *  - quantities diverge                  → local wins (latest on-device intent)
 *  - server line missing locally         → pull it into the store (other device/session)
 */
export function useCartSync() {
  const { status } = useAuth()
  const authed = status === 'authenticated'
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const setQtyLocal = useCartStore((s) => s.setQty)
  const removeLocal = useCartStore((s) => s.remove)
  const addLocal = useCartStore((s) => s.add)

  const cartQuery = useQuery({
    queryKey: ['cart'],
    queryFn: () => api.cart.get(),
    enabled: authed,
    retry: false,
  })

  const lines = useMemo(() => toLines(cartQuery.data), [cartQuery.data])

  /** product_id → server cart line id, for targeting update/remove calls. */
  const serverCartIds = useMemo(() => {
    const map = {}
    lines.forEach((l) => { map[l.product_id] = l.id })
    return map
  }, [lines])

  /** product_id → display facts from the server cart line's product record.
   * The cart page renders from this (API truth) instead of static lists. */
  const serverMeta = useMemo(() => {
    const map = {}
    lines.forEach((l) => {
      const p = l.product
      if (!p) return
      map[String(l.product_id)] = {
        img: p.image_url ?? null,
        category:
          p.categories?.find((c) => c.parent_id === null)?.name ??
          p.categories?.[0]?.name ?? null,
        title: p.name ?? null,
      }
    })
    return map
  }, [lines])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cart'] })
  const syncError = () => showToast('Could not sync your basket with the server.')

  /** Upstream signals business failures as HTTP 200 + { status: false } —
   * rethrow those so they count as rejected, not as a successful push. */
  const ensureOk = (res) => {
    if (res && typeof res === 'object' && res.status === false) {
      throw new Error(res.message || 'Cart operation rejected by server')
    }
    return res
  }

  // One-shot reconciliation per fetch (keyed on dataUpdatedAt so a refetch
  // after our own pushes re-checks convergence without looping on failures).
  const reconciledAt = useRef(0)
  // Push signatures already attempted this session. A push the server accepts
  // but doesn't persist as-asked (200 + status:false, stock clamps, …) would
  // otherwise be regenerated identically after every refetch — fetch → push →
  // invalidate → fetch, forever. One attempt per exact (op, product, qty);
  // a genuinely new local change produces a new signature and syncs normally.
  const attempted = useRef(new Set())
  useEffect(() => {
    if (!authed || !cartQuery.isSuccess) return
    if (reconciledAt.current === cartQuery.dataUpdatedAt) return
    reconciledAt.current = cartQuery.dataUpdatedAt

    const local = useCartStore.getState().items
    const byProduct = new Map(lines.map((l) => [String(l.product_id), l]))
    const jobs = []
    const push = (sig, job) => {
      if (attempted.current.has(sig)) return
      attempted.current.add(sig)
      jobs.push(job().then(ensureOk))
    }

    // Push local state up: create missing lines, correct diverged quantities.
    for (const item of local) {
      const line = byProduct.get(String(item.productId))
      if (!line) {
        const optionIds = cartItemOptionIds(item)
        push(`add:${item.productId}:${item.qty}:${optionIds.join(',')}`, () =>
          api.cart.addToCart({
            product_id: Number(item.productId),
            quantity: item.qty,
            ...(optionIds.length > 0 && { variation_id: optionIds }),
          }))
      } else if (Number(line.quantity) !== item.qty) {
        push(`qty:${line.id}:${item.qty}`, () =>
          api.cart.updateQuantity({ cart_id: line.id, quantity: item.qty }))
      }
    }

    // Refresh catalog facts on local items from their server line: quantity
    // is local intent, but title/price belong to the catalog — a persisted
    // local price must not drift from what the server will actually charge.
    const facts = []
    for (const item of local) {
      const line = byProduct.get(String(item.productId))
      if (!line?.product) continue
      const paise = linePaise(line)
      const title = line.product.name
      if ((paise > 0 && paise !== item.price.amount) || (title && title !== item.title)) {
        facts.push([item.productId, { title: title || item.title, amount: paise > 0 ? paise : item.price.amount }])
      }
    }
    if (facts.length) {
      const byId = new Map(facts)
      useCartStore.setState((s) => ({
        items: s.items.map((i) => {
          const f = byId.get(i.productId)
          return f ? { ...i, title: f.title, price: { ...i.price, amount: f.amount } } : i
        }),
      }))
    }

    // Pull server-only lines down (e.g. added from another device or session).
    const localIds = new Set(local.map((i) => String(i.productId)))
    for (const line of lines) {
      if (localIds.has(String(line.product_id))) continue
      addLocal({
        productId: line.product_id,
        title: line.product?.name ?? 'Product',
        price: { amount: linePaise(line), currency: 'INR' },
        qty: Number(line.quantity) || 1,
      })
    }

    if (jobs.length) {
      Promise.allSettled(jobs).then((results) => {
        // Only refetch when something actually landed — a server that keeps
        // rejecting a push would otherwise refetch/re-push forever.
        if (results.some((r) => r.status === 'fulfilled')) invalidate()
        if (results.some((r) => r.status === 'rejected')) syncError()
      })
    }
  }, [authed, cartQuery.isSuccess, cartQuery.dataUpdatedAt, lines]) // eslint-disable-line react-hooks/exhaustive-deps

  /** True when this product has a server line we can mutate. */
  const isSynced = (productId) =>
    authed && serverCartIds[productId] != null

  /** Set quantity locally and mirror it to the server cart (qty <= 0 removes). */
  const changeQty = (productId, qty) => {
    const cartId = serverCartIds[productId]
    setQtyLocal(productId, qty)
    if (!isSynced(productId)) return
    const req = qty <= 0
      ? api.cart.removeItem({ cart_id: cartId })
      : api.cart.updateQuantity({ cart_id: cartId, quantity: qty })
    req.then(ensureOk).then(invalidate).catch(syncError)
  }

  /** Remove an item locally and from the server cart. */
  const removeItem = (productId) => {
    const cartId = serverCartIds[productId]
    removeLocal(productId)
    if (!isSynced(productId)) return
    api.cart.removeItem({ cart_id: cartId }).then(ensureOk).then(invalidate).catch(syncError)
  }

  return { changeQty, removeItem, serverCartIds, serverMeta, isSyncing: authed && cartQuery.isFetching }
}
